import { getProductPrice, getProductDescription, getProductDiscount } from './configUtilities.jsx';
import { getAllActivePartNumbers, getSoftwareLicensingModel } from './partNumberUtils.jsx';
import { log } from './devLog';

// SW BOM: vduraCare subscriptions, installation services, promotional discounts.
// Rows produced here do NOT carry a `node` field.
export const generateSwBom = (config, productDb, metrics, pricingData, vendor) => {
  const activePartNumbers = getAllActivePartNumbers(config, productDb, metrics, vendor);
  const swModel = getSoftwareLicensingModel(config.serviceOption, vendor);
  const discountOf = (pn) => getProductDiscount(productDb, pn, config.quarter);

  let bom = [];

  // Add SSD software component
  const swSsdPartNum = activePartNumbers.software.vduraCare.ssdPartNumber;
  const swSsdDbPrice = getProductPrice(productDb, config.quarter, swSsdPartNum);
  const ssdVduraCareListPrice = swSsdDbPrice / (1 - discountOf(swSsdPartNum));
  // Calculate actual discount percentage based on the difference between list price and configured price
  const ssdVduraCareDiscountPercent = ((ssdVduraCareListPrice - config.ssdSoftware) / ssdVduraCareListPrice * 100).toFixed(2);
  bom.push({
    partNumber: swSsdPartNum,
    item: getProductDescription(productDb, swSsdPartNum),
    months: config.subscriptionMonths,
    list: Number(ssdVduraCareListPrice).toFixed(2),
    quantity: Math.ceil(metrics.totalSsdCapacityTB / swModel.unitTB),
    discount: `${ssdVduraCareDiscountPercent}%`,
    unitCost: Number(config.ssdSoftware).toFixed(2),
    totalCost: Number(pricingData.software.vduraCare.ssd.total).toFixed(2),
    showInUi: true,
    showInCSV: true,
    parentPartNumber: ''
  });

  // Add the SSD Software License Subcomponent (omitted when service option has no license child)
  const swSsdLicensePartNum = activePartNumbers.software.vduraCareSubcomponents.ssd_license;
  if (swSsdLicensePartNum) {
    const swSsdLicenseListPrice = config.ssdLicenseUnitPrice / (1 - discountOf(swSsdLicensePartNum));
    const swSsdLicenseDiscountPercent = ((swSsdLicenseListPrice - config.ssdLicenseUnitPrice) / swSsdLicenseListPrice * 100).toFixed(2);
    bom.push({
      partNumber: swSsdLicensePartNum,
      item: getProductDescription(productDb, swSsdLicensePartNum),
      months: config.subscriptionMonths,
      list: Number(swSsdLicenseListPrice).toFixed(2),
      quantity: Math.ceil(metrics.totalSsdCapacityTB / swModel.unitTB),
      discount: `${swSsdLicenseDiscountPercent}%`,
      unitCost: Number(config.ssdLicenseUnitPrice).toFixed(2),
      totalCost: Number(pricingData.software.vduraCareSubcomponents.ssd.license),
      showInUi: false,
      showInCSV: true,
      parentPartNumber: swSsdPartNum
    });
  }

  // Add SSD Software Support Subcomponent (omitted when service option has no HW support child)
  const swSsdSupportPartNum = activePartNumbers.software.vduraCareSubcomponents.ssd_support;
  if (swSsdSupportPartNum) {
    const swSsdSupportListPrice = config.ssdSupportUnitPrice / (1 - discountOf(swSsdSupportPartNum));
    const swSsdSupportDiscountPercent = ((swSsdSupportListPrice - config.ssdSupportUnitPrice) / swSsdSupportListPrice * 100).toFixed(2);
    bom.push({
      partNumber: swSsdSupportPartNum,
      item: getProductDescription(productDb, swSsdSupportPartNum),
      months: config.subscriptionMonths,
      list: Number(swSsdSupportListPrice).toFixed(2),
      quantity: Math.ceil(metrics.totalSsdCapacityTB / swModel.unitTB),
      discount: `${swSsdSupportDiscountPercent}%`,
      unitCost: Number(config.ssdSupportUnitPrice).toFixed(2),
      totalCost: Number(pricingData.software.vduraCareSubcomponents.ssd.support),
      showInUi: false,
      showInCSV: true,
      parentPartNumber: swSsdPartNum
    });
  }


  // Add HDD software if hybrid is enabled and has HDDs
  if (config.hybridEnabled && config.hybridHddCapacity > 0) {
    const swHddPartNum = activePartNumbers.software.vduraCare.hddPartNumber;
    const swHddDbPrice = getProductPrice(productDb, config.quarter, swHddPartNum);
    const hddVduraCareListPrice = swHddDbPrice / (1 - discountOf(swHddPartNum));
    // Calculate actual discount percentage based on the difference between list price and configured price
    const hddVduraCareDiscountPercent = ((hddVduraCareListPrice - config.hddSoftware) / hddVduraCareListPrice * 100).toFixed(2);
    bom.push({
      partNumber: swHddPartNum,
      item: getProductDescription(productDb, swHddPartNum),
      months: config.subscriptionMonths,
      list: Number(hddVduraCareListPrice).toFixed(2),
      quantity: Math.ceil(metrics.totalHddCapacityTB / swModel.unitTB),
      discount: `${hddVduraCareDiscountPercent}%`,
      unitCost: Number(config.hddSoftware).toFixed(2),
      totalCost: Number(pricingData.software.vduraCare.hdd.hybrid).toFixed(2),
      showInUi: true,
      showInCSV: true,
      parentPartNumber: ''
    });

    // Add the HDD Software License Subcomponent (omitted when service option has no license child)
    const swHddLicensePartNum = activePartNumbers.software.vduraCareSubcomponents.hdd_license;
    if (swHddLicensePartNum) {
      const swHddLicenseListPrice = config.hddLicenseUnitPrice / (1 - discountOf(swHddLicensePartNum));
      const swHddLicenseDiscountPercent = ((swHddLicenseListPrice - config.hddLicenseUnitPrice) / swHddLicenseListPrice * 100).toFixed(2);
      bom.push({
        partNumber: swHddLicensePartNum,
        item: getProductDescription(productDb, swHddLicensePartNum),
        months: config.subscriptionMonths,
        list: Number(swHddLicenseListPrice).toFixed(2),
        quantity: Math.ceil(metrics.totalHddCapacityTB / swModel.unitTB),
        discount: `${swHddLicenseDiscountPercent}%`,
        unitCost: Number(config.hddLicenseUnitPrice).toFixed(2),
        totalCost: Number(pricingData.software.vduraCareSubcomponents.hdd.license),
        showInUi: false,
        showInCSV: true,
        parentPartNumber: swHddPartNum
      });
    }

    // Add HDD Software Support Subcomponent (omitted when service option has no HW support child)
    const swHddSupportPartNum = activePartNumbers.software.vduraCareSubcomponents.hdd_support;
    if (swHddSupportPartNum) {
      const swHddSupportListPrice = config.hddSupportUnitPrice / (1 - discountOf(swHddSupportPartNum));
      const swHddSupportDiscountPercent = ((swHddSupportListPrice - config.hddSupportUnitPrice) / swHddSupportListPrice * 100).toFixed(2);
      bom.push({
        partNumber: swHddSupportPartNum,
        item: getProductDescription(productDb, swHddSupportPartNum),
        months: config.subscriptionMonths,
        list: Number(swHddSupportListPrice).toFixed(2),
        quantity: Math.ceil(metrics.totalHddCapacityTB / swModel.unitTB),
        discount: `${swHddSupportDiscountPercent}%`,
        unitCost: Number(config.hddSupportUnitPrice).toFixed(2),
        totalCost: Number(pricingData.software.vduraCareSubcomponents.hdd.support),
        showInUi: false,
        showInCSV: true,
        parentPartNumber: swHddPartNum
      });
    }
  }

  // Add discount if applicable
  if (config.discountMonths > 0) {
    const swDiscountPartNum = activePartNumbers.software.discount;
    const swDiscountDbPrice = getProductPrice(productDb, config.quarter, swDiscountPartNum);
    const discountListPrice = swDiscountDbPrice / (1 - discountOf(swDiscountPartNum));
    const discountPercentage = (discountOf(swDiscountPartNum) * 100).toFixed(2);
    bom.push({
      partNumber: swDiscountPartNum,
      item: getProductDescription(productDb, swDiscountPartNum),
      quantity: Math.ceil(metrics.totalRawCapacityTB / 10),
      list: Number(discountListPrice).toFixed(2),
      months: config.discountMonths,
      discount: `${discountPercentage}%`,
      unitCost: getProductPrice(productDb, config.quarter, activePartNumbers.software.discount),
      totalCost: Number(pricingData.software.discounts.promotionalDiscount).toFixed(2),
      showInUi: true,
      showInCSV: true,
      parentPartNumber: ''
    });
  }

  // Add customer integration/installation charge — included for all vendors including software-only
  const installationPartNum = activePartNumbers.services.installation.partNumber;
  const installationDbPrice = getProductPrice(productDb, config.quarter, installationPartNum);
  const installationListPrice = installationDbPrice / (1 - discountOf(installationPartNum));
  const installationDiscountPercentage = (discountOf(installationPartNum) * 100).toFixed(2);
  bom.push({
    partNumber: installationPartNum,
    item: getProductDescription(productDb, installationPartNum),
    quantity: activePartNumbers.services.installation.count,
    discount: `${installationDiscountPercentage}%`,
    unitCost: Number(installationDbPrice).toFixed(2),
    list: Number(installationListPrice).toFixed(2),
    totalCost: Number(pricingData.software.services.installation).toFixed(2),
    showInUi: true,
    showInCSV: true,
    parentPartNumber: ''
  });

  return bom;
};

// HW BOM: director nodes, storage nodes, AFA nodes, drives, NICs.
// Every row carries node: 'D' | 'S' | 'F'.
// The softwareOnly gate is intentionally absent here — callers that need to suppress
// hardware rows (e.g. Dell software-only mode) must filter the result themselves.
// @param {PriceResolver} prices - Resolved price source. REQUIRED, no default:
//   see src/pricing/priceResolver.ts for why omission must throw.
export const generateHwBom = (config, productDb, metrics, pricingData, vendor, prices) => {
  if (typeof prices?.of !== 'function') {
    throw new TypeError(
      'generateHwBom requires a PriceResolver. Pass ' +
      'createPriceResolver(priceBook, quarter, {}) if you mean "no overrides".');
  }
  const activePartNumbers = getAllActivePartNumbers(config, productDb, metrics, vendor);

  const discountOf = (pn) => getProductDiscount(productDb, pn, config.quarter);

  let bom = [];

  // Add Director components if number of directors > 0
  if (config.veloCount > 0) {
    const directorInfo = activePartNumbers.nodes.director;
    const directorNodeDbPrice = prices.of(directorInfo.partNumber);
    const veloDirectorListPrice = directorNodeDbPrice / (1 - discountOf(directorInfo.partNumber));
    const veloDirectorDiscount = (discountOf(directorInfo.partNumber) * 100).toFixed(2);
    bom.push({
      partNumber: directorInfo.partNumber,
      item: getProductDescription(productDb, directorInfo.partNumber),
      node: 'D',
      quantity: config.veloCount,
      list: Number(veloDirectorListPrice).toFixed(2),
      unitCost: Number(directorNodeDbPrice).toFixed(2),
      discount: `${veloDirectorDiscount}%`,
      totalCost: Number(pricingData.hardware.directors.nodes).toFixed(2),
      showInUi: true,
      showInCSV: true,
      parentPartNumber: '',
      isCustomPrice: prices.isCustom(directorInfo.partNumber)
    });

    // Director NIC cards. CONF-93: a dual-port option ships one OCP card per
    // director; a single-port option adds a PCIe card alongside it (the CCI
    // "2x SP" bundles). `pricingData.hardware.directors.networkCards` covers
    // the director's cards as a whole, so it can only BE the row total when
    // there is one row — with two, each row carries its own count x price and
    // the pair still sums to it.
    const directorNics = activePartNumbers.network.director;
    const pushDirectorNicRow = (nic, isOnlyRow) => {
      const nicDbPrice = prices.of(nic.partNumber);
      const nicListPrice = nicDbPrice / (1 - discountOf(nic.partNumber));
      const nicDiscount = (discountOf(nic.partNumber) * 100).toFixed(2);
      bom.push({
        partNumber: nic.partNumber,
        item: getProductDescription(productDb, nic.partNumber),
        node: 'D',
        quantity: nic.count,
        list: Number(nicListPrice).toFixed(2),
        unitCost: Number(nicDbPrice).toFixed(2),
        discount: `${nicDiscount}%`,
        totalCost: Number(isOnlyRow ? pricingData.hardware.directors.networkCards
          : nic.count * nicDbPrice).toFixed(2),
        showInUi: true,
        showInCSV: true,
        parentPartNumber: '',
        isCustomPrice: prices.isCustom(nic.partNumber)
      });
    };
    pushDirectorNicRow(directorNics.ocp, !directorNics.pcie);
    if (directorNics.pcie) {
      pushDirectorNicRow(directorNics.pcie, false);
    }

    // TODO : Verify if 12 magic number is still needed or if it is ssdsPerVelo
    const directorSsdPartNum = activePartNumbers.drives.director.ssdPartNumber;
    const directorSsdDbPrice = prices.of(directorSsdPartNum);
    const veloSsdListPrice = directorSsdDbPrice / (1 - discountOf(directorSsdPartNum));
    const veloSsdDiscount = (discountOf(directorSsdPartNum) * 100).toFixed(2);
    bom.push({
      partNumber: directorSsdPartNum,
      item: getProductDescription(productDb, directorSsdPartNum),
      node: 'D',
      quantity: config.veloCount * productDb.ssdPerVelo,
      list: Number(veloSsdListPrice).toFixed(2),
      unitCost: Number(directorSsdDbPrice).toFixed(2),
      discount: `${veloSsdDiscount}%`,
      totalCost: Number(pricingData.hardware.directors.ssds).toFixed(2),
      showInUi: true,
      showInCSV: true,
      parentPartNumber: '',
      isCustomPrice: prices.isCustom(directorSsdPartNum)
    });
  }

  // Add Hybrid storage components if enabled
  if (config.hybridEnabled) {
    const hybridStorageNodePartNum = activePartNumbers.nodes.hybrid.partNumber;
    const hybridStorageNodeDbPrice = prices.of(hybridStorageNodePartNum);
    const hybridStorageNodeListPrice = hybridStorageNodeDbPrice / (1 - discountOf(hybridStorageNodePartNum));
    const hybridStorageNodeDiscount = (discountOf(hybridStorageNodePartNum) * 100).toFixed(2);
    bom.push({
      partNumber: hybridStorageNodePartNum,
      item: getProductDescription(productDb, hybridStorageNodePartNum),
      node: 'S',
      quantity: config.hybridNodeCount,
      list: Number(hybridStorageNodeListPrice).toFixed(2),
      discount: `${hybridStorageNodeDiscount}%`,
      unitCost: Number(hybridStorageNodeDbPrice).toFixed(2),
      totalCost: Number(pricingData.hardware.hybrid.nodes).toFixed(2),
      showInUi: true,
      showInCSV: true,
      parentPartNumber: '',
      isCustomPrice: prices.isCustom(hybridStorageNodePartNum)
    });

    // Add Hybrid network adapters
    // Hybrid Storage - PCIe Network Card
    const hybridPciePartNum = activePartNumbers.network.hybrid.pcie.partNumber;
    const hybridPcieDbPrice = prices.of(hybridPciePartNum);
    const hybridPcieListPrice = hybridPcieDbPrice / (1 - discountOf(hybridPciePartNum));
    const hybridPcieDiscount = (discountOf(hybridPciePartNum) * 100).toFixed(2);
    bom.push({
      partNumber: hybridPciePartNum,
      item: getProductDescription(productDb, hybridPciePartNum),
      node: 'S',
      quantity: config.hybridNodeCount,
      list: Number(hybridPcieListPrice).toFixed(2),
      unitCost: Number(hybridPcieDbPrice).toFixed(2),
      discount: `${hybridPcieDiscount}%`,
      totalCost: Number(config.hybridNodeCount * hybridPcieDbPrice).toFixed(2),
      showInUi: true,
      showInCSV: true,
      parentPartNumber: '',
      isCustomPrice: prices.isCustom(hybridPciePartNum)
    });

    // Hybrid Storage - OCP Network Card
    const hybridOcpPartNum = activePartNumbers.network.hybrid.ocp.partNumber;
    const hybridOcpDbPrice = prices.of(hybridOcpPartNum);
    const hybridOcpListPrice = hybridOcpDbPrice / (1 - discountOf(hybridOcpPartNum));
    const hybridOcpDiscount = (discountOf(hybridOcpPartNum) * 100).toFixed(2);
    bom.push({
      partNumber: hybridOcpPartNum,
      item: getProductDescription(productDb, hybridOcpPartNum),
      node: 'S',
      quantity: config.hybridNodeCount,
      list: Number(hybridOcpListPrice).toFixed(2),
      unitCost: Number(hybridOcpDbPrice).toFixed(2),
      discount: `${hybridOcpDiscount}%`,
      totalCost: Number(config.hybridNodeCount * hybridOcpDbPrice).toFixed(2),
      showInUi: true,
      showInCSV: true,
      parentPartNumber: '',
      isCustomPrice: prices.isCustom(hybridOcpPartNum)
    });

    // Add Hybrid SSDs
    const hybridSsdPartNum = activePartNumbers.drives.hybrid.ssdPartNumber;
    const hybridSsdDbPrice = prices.of(hybridSsdPartNum);
    const hybridSsdListPrice = hybridSsdDbPrice / (1 - discountOf(hybridSsdPartNum));
    const hybridSsdDiscount = (discountOf(hybridSsdPartNum) * 100).toFixed(2);
    bom.push({
      partNumber: hybridSsdPartNum,
      item: getProductDescription(productDb, hybridSsdPartNum),
      node: 'S',
      quantity: config.hybridNodeCount * 12,
      list: Number(hybridSsdListPrice).toFixed(2),
      discount: `${hybridSsdDiscount}%`,
      unitCost: Number(hybridSsdDbPrice).toFixed(2),
      totalCost: Number(pricingData.hardware.hybrid.ssds).toFixed(2),
      showInUi: true,
      showInCSV: true,
      parentPartNumber: '',
      isCustomPrice: prices.isCustom(hybridSsdPartNum)
    });

    // Add HDDs if applicable
    if (config.hybridHddCapacity > 0) {
      const hybridHddPartNum = activePartNumbers.drives.hybrid.hddPartNumber;
      const hybridHddDbPrice = prices.of(hybridHddPartNum);
      const hddListPrice = hybridHddDbPrice / (1 - discountOf(hybridHddPartNum));
      const hddDiscount = (discountOf(hybridHddPartNum) * 100).toFixed(2);
      bom.push({
        partNumber: hybridHddPartNum,
        item: getProductDescription(productDb, hybridHddPartNum),
        node: 'S',
        quantity: config.hybridNodeCount,
        list: Number(hddListPrice).toFixed(2),
        discount: `${hddDiscount}%`,
        unitCost: Number(hybridHddDbPrice).toFixed(2),
        totalCost: Number(pricingData.hardware.hybrid.hdds).toFixed(2),
        showInUi: true,
        showInCSV: true,
        parentPartNumber: '',
        isCustomPrice: prices.isCustom(hybridHddPartNum)
      });
    }
  }

  // Add AFA storage components if enabled
  if (config.afaEnabled) {
    const afaNodePartNumber = activePartNumbers.nodes.afa.partNumber;
    const afaNodeDbPrice = prices.of(afaNodePartNumber);
    const afaStorageNodeListPrice = afaNodeDbPrice / (1 - discountOf(afaNodePartNumber));
    const afaStorageNodeDiscount = (discountOf(afaNodePartNumber) * 100).toFixed(2);
    bom.push({
      partNumber: afaNodePartNumber,
      item: getProductDescription(productDb, afaNodePartNumber),
      node: 'F',
      quantity: config.afaNodeCount,
      list: Number(afaStorageNodeListPrice).toFixed(2),
      discount: `${afaStorageNodeDiscount}%`,
      unitCost: Number(afaNodeDbPrice).toFixed(2),
      totalCost: Number(pricingData.hardware.afa.nodes).toFixed(2),
      showInUi: true,
      showInCSV: true,
      parentPartNumber: '',
      isCustomPrice: prices.isCustom(afaNodePartNumber)
    });

    // AFA Storage - network cards. CONF-210: the flash node's NIC counts come
    // from its model (the Dual CPU node carries 2 PCIe + 1 OCP per node), so
    // each row carries the resolved CARD count and its own count x price —
    // the pushDirectorNicRow pattern — and the pair sums back to the booked
    // pricingData.hardware.afa.networkCards figure.
    const afaNics = activePartNumbers.network.afa;
    const pushAfaNicRow = (nic) => {
      const nicDbPrice = prices.of(nic.partNumber);
      const nicListPrice = nicDbPrice / (1 - discountOf(nic.partNumber));
      const nicDiscount = (discountOf(nic.partNumber) * 100).toFixed(2);
      bom.push({
        partNumber: nic.partNumber,
        item: getProductDescription(productDb, nic.partNumber),
        node: 'F',
        quantity: nic.count,
        list: Number(nicListPrice).toFixed(2),
        unitCost: Number(nicDbPrice).toFixed(2),
        discount: `${nicDiscount}%`,
        totalCost: Number(nic.count * nicDbPrice).toFixed(2),
        showInUi: true,
        showInCSV: true,
        parentPartNumber: '',
        isCustomPrice: prices.isCustom(nic.partNumber)
      });
    };
    pushAfaNicRow(afaNics.pcie);
    pushAfaNicRow(afaNics.ocp);

    const afaSsdPartNumber = activePartNumbers.drives.afa.ssdPartNumber;
    const afaSsdDbPrice = prices.of(afaSsdPartNumber);
    const afaSsdListPrice = afaSsdDbPrice / (1 - discountOf(afaSsdPartNumber));
    const afaSsdDiscount = (discountOf(afaSsdPartNumber) * 100).toFixed(2);
    bom.push({
      partNumber: afaSsdPartNumber,
      item: getProductDescription(productDb, afaSsdPartNumber),
      node: 'F',
      quantity: config.afaNodeCount * 12,
      list: Number(afaSsdListPrice).toFixed(2),
      discount: `${afaSsdDiscount}%`,
      unitCost: Number(afaSsdDbPrice).toFixed(2),
      totalCost: Number(pricingData.hardware.afa.ssds).toFixed(2),
      showInUi: true,
      showInCSV: true,
      parentPartNumber: '',
      isCustomPrice: prices.isCustom(afaSsdPartNumber)
    });
  }

  return bom;
};

// BOM Generator function that takes configuration and productDb as input.
// Thin combiner: merges SW and HW rows, preserves the softwareOnly gate for
// the total row, then returns the same shape as before.
// @param {PriceResolver} prices - Resolved price source. REQUIRED, no default:
//   see src/pricing/priceResolver.ts for why omission must throw. HARDWARE SKUs
//   only — threaded into generateHwBom. Software rows (VduraCare, discounts,
//   installation) always stay catalog-priced.
export const generateBom = (config, productDb, metrics, pricingData, vendor, prices) => {
  if (typeof prices?.of !== 'function') {
    throw new TypeError(
      'generateBom requires a PriceResolver. Pass ' +
      'createPriceResolver(priceBook, quarter, {}) if you mean "no overrides".');
  }
  // When true, only software subscription items are included; hardware is fulfilled by the vendor partner
  const softwareOnly = vendor?.businessRules?.softwareSubscriptionOnly === true;

  log("=== BOM GENERATION BEGIN ===");

  const swRows = generateSwBom(config, productDb, metrics, pricingData, vendor);

  // For software-only vendors, omit hardware rows entirely (gate preserved at output layer)
  const hwRows = softwareOnly ? [] : generateHwBom(config, productDb, metrics, pricingData, vendor, prices);

  const separatorRow = { item: '__sw_hw_separator__', showInUi: true };
  let bom = softwareOnly ? [...swRows] : [...swRows, separatorRow, ...hwRows];

  // Calculate total solution cost by summing all line items
  let calculatedTotalCost = 0;
  bom.forEach(item => {
    if (item.totalCost !== undefined && !isNaN(Number(item.totalCost))) {
      calculatedTotalCost += Number(item.totalCost);
    }
  });

  // Ensure the total solution cost is a number
  calculatedTotalCost = Number(calculatedTotalCost);

  // Sanity check of compared BOM cost and calculated pricing calculator total
  if( Math.abs(calculatedTotalCost - pricingData.solution.grandTotal) > 0.01) {
    console.warn('BOM total cost does not match calculated pricing total');
  }

  // Add total row — software-only vendors quote software cost only
  const solutionTotal = softwareOnly ? pricingData.software.total : pricingData.solution.grandTotal;
  bom.push(
    {}, // Empty row for spacing
    {
      item: 'Solution Price',
      totalCost: Number(solutionTotal).toFixed(2)
    }
  );

  log("=== BOM GENERATION COMPLETE ===");
  // Return both the BOM array and the calculated total cost
  return { bom, calculatedTotalCost };
};
